import { useMemo, useState } from 'react'
import { router } from '@inertiajs/react'
import { SearchX } from 'lucide-react'
import { applyTableState, useTableState, DataTableToolbar } from '@/components/blocks/data-table'
import { Button } from '@/components/ui/Button'
import { EmptyState } from '@/components/ui/EmptyState'
import { useToast } from '@/components/ui/Toast'
import { PLAYER_COLUMNS, PLAYER_FILTERS, PlayersTable } from '@/components/players'
import { ConfirmDialog, type ConfirmRequest, PageHeader } from '@/components/shared'
import { PLAYERS } from '@/fixtures'
import type { Player, PlayerStatus } from '@/models'

const TABLE_INITIAL = { pageSize: 10, sortKey: 'trophies', sortDir: 'desc' as const }

export function PlayersPage() {
  const { toast } = useToast()
  const [players, setPlayers] = useState<Player[]>(PLAYERS)
  const [confirm, setConfirm] = useState<ConfirmRequest | null>(null)
  const { state, setState } = useTableState(TABLE_INITIAL)

  const table = useMemo(() => applyTableState(players, PLAYER_COLUMNS, state), [players, state])

  const setStatus = (player: Player, status: PlayerStatus) => {
    setPlayers((previous) =>
      previous.map((row) => (row.id === player.id ? { ...row, status } : row)),
    )
    toast(
      `${player.username} is now ${status.toLowerCase()}`,
      status === 'Active' ? 'success' : 'danger',
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Players"
        description={`${players.length} registered players. Click a row for details and admin actions.`}
      />

      {/* Toolbar and table are one unit: 12px apart, 24px from everything
          else. Same on Gift Codes and Leaderboard. */}
      <div className="space-y-3">
        <DataTableToolbar
          state={state}
          onStateChange={setState}
          searchPlaceholder="Search by ID or username…"
          filters={PLAYER_FILTERS}
        />

        <PlayersTable
          empty={
            <EmptyState
              icon={<SearchX />}
              title="No players match your filters."
              action={
                <Button variant="outline" onClick={() => setState({ search: '', filters: {} })}>
                  Clear Filters
                </Button>
              }
            />
          }
          rows={table.rows}
          total={table.total}
          state={state}
          onStateChange={setState}
          onOpen={(player) => router.visit(`/players/${player.id}`)}
          onSuspend={(player) =>
            setStatus(player, player.status === 'Suspended' ? 'Active' : 'Suspended')
          }
          onBan={(player) =>
            player.status === 'Banned'
              ? setStatus(player, 'Active')
              : setConfirm({
                  title: `Ban ${player.username}?`,
                  description:
                    'They lose access immediately and their trophies are frozen. This can be undone from the same menu.',
                  confirmLabel: 'Ban Player',
                  destructive: true,
                  onConfirm: () => setStatus(player, 'Banned'),
                })
          }
        />
      </div>

      <ConfirmDialog request={confirm} onClose={() => setConfirm(null)} />
    </div>
  )
}
